/* eslint-disable @typescript-eslint/ban-ts-comment */
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { ISelectValue } from 'constants/globalTypes'
import { getSingleProcessTemplate } from 'features/processTemplates/processTemplatesSlice'
import { components } from 'pages/TaskTemplates/taskData'
import React from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { Flex, ItemsGrid, ThinSeparator } from 'styling/GlobalStyles'
import { IProcessTemplatesSingleResponse } from 'types/api/processTemplatesApi.interface'
import { IPhaseState, IProcessBindingConfigState } from 'types/operationPlans.interface'

type ProcessTemplateTypeProps = {
    formKey: string | number
    addingPhase: IPhaseState<IProcessBindingConfigState>
    setAddingPhase: React.Dispatch<React.SetStateAction<IPhaseState<IProcessBindingConfigState>>>
}

export const ProcessTemplateType = ({ formKey, addingPhase, setAddingPhase }: ProcessTemplateTypeProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const bindingConfig = addingPhase.bindingConfig ?? ({} as IProcessBindingConfigState)

    const parse = (data: any) => {
        return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
    }

    const updateBindingConfig = (patch: Partial<IProcessBindingConfigState>) => {
        setAddingPhase((prev) => ({
            ...prev,
            bindingConfig: { ...(prev.bindingConfig ?? {}), ...patch } as IProcessBindingConfigState,
        }))
    }

    const handleProcessSelect = async (value: ISelectValue) => {
        const resp = await dispatch(getSingleProcessTemplate(value.value.toString()))
        if (resp.meta.requestStatus === 'fulfilled') {
            setAddingPhase((prev) => ({
                ...prev,
                processTemplate: { label: value.label, value: value.value },
                bindingConfig: {
                    startDate: null,
                    department: null,
                    location: null,
                    componentsData: (resp.payload as IProcessTemplatesSingleResponse).components.map((component) => ({
                        processTemplateComponent: { label: component.name, value: component.id },
                        componentType: component.component,
                        value: null,
                    })),
                },
            }))
        }
    }

    const handleComponentInput = (e: React.ChangeEvent<HTMLInputElement>, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        const newValue = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.processTemplateComponent?.value === componentId ? { ...c, value: newValue } : c,
            ),
        })
    }

    const handleComponentSelect = (value: ISelectValue, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.processTemplateComponent?.value === componentId ? { ...c, value } : c,
            ),
        })
    }

    const handleComponentDate = (date: Date, componentId: number | string) => {
        const config = addingPhase.bindingConfig
        if (!config?.componentsData) return
        updateBindingConfig({
            componentsData: config.componentsData.map((c) =>
                c.processTemplateComponent?.value === componentId ? { ...c, value: date } : c,
            ),
        })
    }

    const renderComponentInput = (component: IComponent) => {
        const foundRenderInput = components.find(c => c.component === component.componentType)

        let value
        const componentId = component.processTemplateComponent?.value
        const fn = foundRenderInput?.inputType == 3 ? handleComponentDate : [4, 5].includes(foundRenderInput?.inputType) ? handleComponentSelect : handleComponentInput

        switch (foundRenderInput?.inputType) {
            case 1:
                value = component.value
                break
            case 2:
                value = Number(component.value)
                break
            case 3:
                value = new Date(String(component.value))
                break
            case 4:
                value = (component.value as ISelectValue)?.value
                break
            case 5:
                value = (component.value as ISelectValue)?.label
        }

        return foundRenderInput?.renderInput({ handleChange: (e) => fn(e, componentId), value, name: 'value', labelText: component?.processTemplateComponent?.label })
    }

    return <>
        <Flex column gap='1rem' >
            <HonestSelectAsyncInput
                key={`process-template-${formKey}`}
                keyProp={2}
                defaultValue={addingPhase.processTemplate?.label}
                link='process/template/company'
                labelText='Task Process Template'
                onChange={handleProcessSelect}
                name='processTemplate'
                placeholder='Search Process Templates'
                parserFunction={parse}
                required
                invalid={!addingPhase.processTemplate?.value}
            />
            {addingPhase.processTemplate?.value && <>
                <Flex gap='1rem' >
                    <CalendarInput
                        key={`process-startDate-${formKey}`}
                        labelText='Start Date'
                        name='startDate'
                        defaultValue={bindingConfig.startDate ?? null}
                        onChange={(date) => updateBindingConfig({ startDate: date })}
                        width='100%'
                    />
                </Flex>
                <HonestSelectAsyncInput
                    key={`process-location-${formKey}`}
                    keyProp={2}
                    nomarg
                    link='locations'
                    placeholder='Search locations'
                    name='location'
                    labelText='Location'
                    defaultValue={bindingConfig.location?.label}
                    onChange={(value) => updateBindingConfig({ location: value })}
                    parserFunction={(data: any) => data.data.map((i: any) => ({ label: i.name, value: i.id }))}
                    required
                    invalid={!bindingConfig.location?.value}
                />
                <HonestSelectAsyncInput
                    key={`process-department-${formKey}`}
                    keyProp={2}
                    link='departments'
                    placeholder='Search department'
                    name='department'
                    labelText='Department'
                    defaultValue={bindingConfig.department?.label}
                    onChange={(value) => updateBindingConfig({ department: value })}
                    parserFunction={(data: any) =>
                        data.data.map((i: any) => ({ label: i.name, value: i.id }))
                    }
                />
                <ThinSeparator />
                <ItemsGrid columns={2} >
                    {bindingConfig.componentsData?.map((c) => renderComponentInput(c))}
                </ItemsGrid>
            </>}
        </Flex>

    </>
}


interface IComponent {
    processTemplateComponent: ISelectValue;
    componentType: number;
    value: number | Date | ISelectValue | string
}
